import { over, times } from 'lodash';
import T from 'prop-types';
import React, { useMemo, useRef, useState } from 'react';
import InputMask from 'react-input-mask';

import { be, bm } from '@skippay/core/utils/classUtils';
import { getDecimalSeparator } from '@skippay/core/utils/numberUtils';
import { filterPattern } from '@skippay/core/utils/string';

import './index.sass';
import { unitsSpacing } from '../utils';

const MODULE_NAME = 'TypingInput'; // historically this input was called Typing

const preventTypingUnsupportedDecimalSeparatorInNumberInput = (e) => {
	const decimalSeparator = getDecimalSeparator();
	const keyCode = e.which ? e.which : e.keyCode;
	if (
		(decimalSeparator === ',' && (keyCode === 190 || keyCode === 110 || keyCode === 46)) ||
		(decimalSeparator === '.' && keyCode === 188)
	) {
		e.preventDefault();
	}
};

function TextInput(props) {
	const {
		animatedInput,
		center,
		hasError,
		hasValue,
		highlighted,
		icon,
		inputMask,
		inputMaskAlwaysShow,
		inputMaskChar,
		inputPattern,
		invertedPlaceholderColor,
		hasSensitiveContent,
		large,
		larger,
		letterSpacingUnderlined,
		maxLength,
		meta, // eslint-disable-line
		multiline,
		multilineSmall,
		onInput,
		onRef,
		pinNumber,
		placeholder,
		readOnly,
		search,
		type = 'text',
		units,
		wide,
		onChange,
		...otherProps
	} = props;

	const InputComponent =
		type === 'text' && (multiline || multilineSmall) ? 'textarea' : inputMask ? InputMask : 'input';

	const [prevValue, setPrevValue] = useState('');

	const handleInput = useMemo(() => {
		const inputFilter = inputPattern && filterPattern(inputPattern);
		return over([
			inputFilter &&
				(({ target }) => {
					if (target.value) {
						// eslint-disable-next-line no-param-reassign
						target.value = inputFilter(target.value);

						if (prevValue !== target.value) {
							setPrevValue(target.value);
						}
					}
				}),
			onInput,
		]);
	}, [inputPattern, onInput, prevValue]);

	const containerRef = useRef(null);

	return (
		<div
			ref={containerRef}
			className={bm(
				MODULE_NAME,
				{
					animatedInput,
					hasError,
					highlighted,
					larger,
					letterSpacingUnderlined,
					center,
					pinNumber,
					search,
					readOnly,
					wide,
					multilineSmall,
				},
				[(hasValue || props.value || props.defaultValue) && 'hasValue']
			)}
		>
			{icon && <div className={be(MODULE_NAME, 'icon')}>{icon}</div>}
			<InputComponent
				{...otherProps}
				{...(inputMask && {
					alwaysShowMask: inputMaskAlwaysShow,
					mask: inputMask,
					maskChar: inputMaskChar,
				})}
				ref={onRef}
				className={be(MODULE_NAME, 'input', { large, withIcon: Boolean(icon) }, props.className)}
				maxLength={maxLength}
				onInput={handleInput}
				onChange={onChange}
				type={type}
				readOnly={readOnly}
				arial-label={placeholder}
				aria-required={Boolean(props.required)}
				placeholder={(!animatedInput && placeholder) || ''}
				onKeyDown={(e) => {
					// eslint-disable-next-line sonarjs/no-collapsible-if
					if (type === 'number') {
						preventTypingUnsupportedDecimalSeparatorInNumberInput(e);
					}
				}}
				{...(hasSensitiveContent && { 'data-clarity-mask': 'true' })}
			/>
			{animatedInput && <div className={be(MODULE_NAME, 'placeholder')}>{placeholder}</div>}
			{units && (
				<div
					className={be(MODULE_NAME, 'units', { inverted: invertedPlaceholderColor })}
					style={{
						left: `${unitsSpacing({
							value: props.value || prevValue,
							inputSize: containerRef?.current?.offsetWidth,
						})}px`,
					}}
				>
					{units}
				</div>
			)}
			{pinNumber && maxLength && (
				<div className={be(MODULE_NAME, 'dots')}>
					{times(maxLength - props.value.length, (idx) => (
						<span key={idx} className={be(MODULE_NAME, 'dot')} />
					))}
				</div>
			)}
		</div>
	);
}

TextInput.propTypes = {
	/**
	 * Turns on animated placeholder (Android like)
	 */
	animatedInput: T.bool,
	center: T.bool,
	className: T.string,
	/**
	 * _Only for uncontrolled component._
	 * @see https://reactjs.org/docs/uncontrolled-components.html
	 */
	defaultValue: T.string,
	disabled: T.bool,
	/**
	 * Use character placeholders
	 *
	 * 	9: 0-9
	 * 	a: A-Z, a-z
	 * 	*: A-Z, a-z, 0-9
	 *
	 * 	to define an input mask. E.g. `99-99-9999` for data input. All characters other than 9, a, *,
	 * 	will be left in the input as a mask. To insert character placeholder as literal, escape it
	 * 	with backslash in JSX or two backslashes in JS string (e.g. `mask="\9"` vs. `mask={'\\9'}`).
	 */
	inputMask: T.string,
	inputMaskAlwaysShow: T.bool,
	/**
	 * Character used for unfilled parts of input mask.
	 */
	inputMaskChar: T.string,
	inputPattern: T.string,
	invertedPlaceholderColor: T.bool,
	large: T.bool,
	larger: T.bool,
	letterSpacingUnderlined: T.bool,
	maxLength: T.number,
	/**
	 * Enables large and resizable text input. Type of input must be `text`, does not support
	 * an `inputMask`.
	 */
	multiline: T.bool,
	multilineSmall: T.bool,
	name: T.string,
	onChange: T.func,
	pinNumber: T.bool,
	placeholder: T.string,
	readOnly: T.bool,
	required: T.bool,
	type: T.oneOf(['email', 'hidden', 'number', 'password', 'search', 'tel', 'text']),
	units: T.node,
	value: T.string,
	wide: T.bool,
	autoSelectOnClick: T.bool,
	hasSensitiveContent: T.bool,
};

TextInput.defaultProps = {
	inputMaskChar: '_',
};

export default TextInput;
